import { useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useResizeObserver } from "@reactuses/core";
import type { ComboBoxProps as RacComboBoxProps, ListBoxLoadMoreItemProps } from "react-aria-components";
import {
  Collection,
  ComboBox as ComboBoxBase,
  FieldError,
  Group,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  ListBoxLoadMoreItem,
  ProgressBar,
  Popover,
  Text,
  useFilter,
} from "react-aria-components";
import { useTranslation } from "react-i18next";
import { css, cx } from "../../../../styled-system/css";
import type { SystemStyleObject } from "../../../../styled-system/types";
import { formField, type FormFieldVariant } from "../../../../styled-system/recipes";
import { popoverStyles } from "../styles/styles";
import { ImSpinner2 } from "react-icons/im";

type ComboboxOption = {
  label: string;
  value: string | number | boolean;
  disabled?: boolean;
  isDisabled?: boolean;
};

const listBoxStyles = css({
  maxHeight: "240px",
  paddingY: "xsmall",
  scrollbarColor: "{colors.lightgrey} transparent",
  scrollbarGutter: "stable both-edges",
  scrollbarWidth: "thin",
  overflowY: "auto",
  "& [data-hovered]:not([data-selected]):not([data-focused])": {
    color: "darkblue",
    backgroundColor: "black/10",
  },
});

const loadMoreStyles = css({
  cursor: "default",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  paddingY: "xsmall",
  paddingX: "medium",
  color: "text.secondary",
  fontSize: "small",
});

const comboboxItemClassName = css({
  cursor: "pointer",
  paddingY: "xsmall",
  paddingX: "medium",
  color: "darkblue/80",
  fontSize: "small",
  backgroundColor: "white",
  _disabled: {
    cursor: "not-allowed",
    color: "text.secondary",
    backgroundColor: "white",
  },
  _selected: {
    color: "white",
    backgroundColor: "trellisPalette.4/80",
  },
  _focusVisible: { outline: "none" },
  "&[data-focused]": {
    color: "darkblue",
    backgroundColor: "black/10",
  },
});

const loadingIndicatorStyles = css({
  display: "flex",
  alignItems: "center",
  color: "text.secondary",
});

const spinnerAnimation = css({
  animation: "spin",
});

const emptyStateStyles = css({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  paddingY: "small",
  paddingX: "medium",
  fontSize: "small",
  color: "text.secondary",
});

export function ComboboxLoadMoreItem({ className, children, ...props }: ListBoxLoadMoreItemProps) {
  const { t } = useTranslation(["common"]);
  return (
    <ListBoxLoadMoreItem {...props} className={cx(loadMoreStyles, className)}>
      {children ?? t(($) => $.common["Loading more..."])}
    </ListBoxLoadMoreItem>
  );
}

interface Props<T extends object> extends Omit<RacComboBoxProps<T>, "children" | "className"> {
  css?: SystemStyleObject;
  label?: ReactNode;
  description?: string;
  errorMessage?: string;
  items: ReadonlyArray<T>;
  children?: (item: T) => ReactNode;
  /** Formily seems to mess up children, so this is a workaround to allow it to pass custom renderer */
  itemRenderer?: (item: T) => ReactNode;
  variant?: FormFieldVariant["variant"];
  placeholder?: string;
  listBoxFooter?: ReactNode;
  isLoading?: boolean;
  emptyMessage?: ReactNode;
}

export function Combobox<T extends object>({
  label,
  description,
  errorMessage,
  children,
  itemRenderer,
  variant,
  placeholder,
  listBoxFooter,
  isLoading,
  emptyMessage,
  css: cssProp,
  menuTrigger = "focus",
  allowsEmptyCollection = true,
  defaultFilter,
  isInvalid,
  items,
  ...props
}: Props<T>) {
  const { t } = useTranslation(["common"]);
  const classes = formField({ variant });
  const { contains } = useFilter({ sensitivity: "base" });
  const resolvedEmptyMessage = emptyMessage ?? t(($) => $.common["No results"]);

  const groupRef = useRef<HTMLDivElement>(null);
  const [groupWidth, setGroupWidth] = useState<string>();

  useResizeObserver(groupRef, () => {
    if (groupRef.current) setGroupWidth(`${groupRef.current.offsetWidth}px`);
  });

  const resolvedChildren = children ?? itemRenderer ?? defaultRenderer;

  return (
    <ComboBoxBase
      className={cx(classes.fieldWrapper, css(cssProp))}
      menuTrigger={menuTrigger}
      allowsEmptyCollection={allowsEmptyCollection}
      defaultFilter={defaultFilter ?? contains}
      isInvalid={isInvalid ?? !!errorMessage}
      {...props}
    >
      {label && <Label className={classes.label}>{label}</Label>}
      <Group ref={groupRef} className={classes.inputBox}>
        <Input className={classes.input} placeholder={placeholder} />
        {isLoading && (
          <ProgressBar aria-label={t(($) => $.common["Loading..."])} isIndeterminate className={loadingIndicatorStyles}>
            <ImSpinner2 className={spinnerAnimation} />
          </ProgressBar>
        )}
      </Group>
      {description && <Text slot="description">{description}</Text>}
      {errorMessage && <FieldError className={classes.error}>{errorMessage}</FieldError>}
      <Popover
        triggerRef={groupRef}
        placement="bottom"
        offset={4}
        style={{ "--combobox-group-width": groupWidth } as CSSProperties}
        className={cx(popoverStyles, css({ width: "var(--combobox-group-width)", paddingY: "xsmall" }))}
      >
        <ListBox
          className={listBoxStyles}
          renderEmptyState={() => (
            <div className={emptyStateStyles}>
              {isLoading ?
                <ProgressBar
                  aria-label={t(($) => $.common["Loading..."])}
                  isIndeterminate
                  className={loadingIndicatorStyles}
                >
                  <ImSpinner2 className={spinnerAnimation} />
                </ProgressBar>
              : resolvedEmptyMessage}
            </div>
          )}
        >
          <Collection items={items}>{resolvedChildren}</Collection>
          {listBoxFooter}
        </ListBox>
      </Popover>
    </ComboBoxBase>
  );

  function defaultRenderer(item: object) {
    if (
      "label" in item &&
      "value" in item &&
      (typeof item.value === "string" || typeof item.value === "number" || typeof item.value === "boolean") &&
      typeof item.label === "string"
    ) {
      const { label, value, isDisabled, disabled } = item as ComboboxOption;
      return (
        <ListBoxItem
          id={value as string}
          textValue={label}
          isDisabled={isDisabled ?? disabled}
          className={comboboxItemClassName}
        >
          {label || "%missing_label%"}
        </ListBoxItem>
      );
    }
    throw new Error("You need a custom renderer");
  }
}
